import React, { useEffect, useState } from 'react';

export type FlagState = {
  enabled: boolean;
  loading: boolean;
};

export type FlagEvaluator = (flag: string) => FlagState;
export type AsyncFlagEvaluator = (flag: string) => Promise<boolean>;

export interface FeatureFlagGateProps {
  /** Feature flag key to evaluate */
  flag: string;
  /** Consumer-provided evaluator that resolves flag state synchronously */
  evaluate: FlagEvaluator;
  /** Rendered when flag is enabled */
  enabled: React.ReactNode;
  /** Rendered when flag is disabled */
  disabled: React.ReactNode;
  /**
   * Rendered while the flag is loading. Falls back to `disabled` when not
   * passed. When `disabled` is an interactive tree (a form, etc.), pass an
   * explicit loader/skeleton so the user can't start typing into the
   * `disabled` tree only to lose that in-flight state when the flag
   * resolves `enabled` and React swaps trees.
   */
  loading?: React.ReactNode;
  /** Force-disable the gate regardless of flag state */
  forceDisabled?: boolean;
}

export interface AsyncFeatureFlagGateProps extends Omit<FeatureFlagGateProps, 'evaluate'> {
  /**
   * Consumer-provided evaluator that resolves to whether the flag is enabled.
   * The library owns loading state — the promise pending is the loading signal.
   * Memoize with `useCallback` to avoid re-firing the resolver on every render.
   */
  evaluate: AsyncFlagEvaluator;
}

/**
 * Use when a flag has a default and the values for enabled / disabled are set by AuthFE
 *
 * eg. downstream consumer
 * const evaluateAccountFormFlag = ...;
 * <AccountForm evaluate={evaluateAccountFormFlag} />
 */
export type DefaultFeatureFlagProps = Omit<FeatureFlagGateProps, 'flag' | 'enabled' | 'disabled'> &
  Partial<Pick<FeatureFlagGateProps, 'flag'>>;
export type DefaultAsyncFeatureFlagProps = Omit<
  AsyncFeatureFlagGateProps,
  'flag' | 'enabled' | 'disabled'
> &
  Partial<Pick<AsyncFeatureFlagGateProps, 'flag'>>;

export const FeatureFlagGate: React.FC<FeatureFlagGateProps> = ({
  flag,
  evaluate,
  enabled: enabledComponent,
  disabled: disabledComponent,
  loading: loadingComponent,
  forceDisabled = false,
}) => {
  if (forceDisabled) {
    return disabledComponent;
  }

  const { enabled, loading } = evaluate(flag);

  if (loading) {
    return loadingComponent ?? disabledComponent;
  }

  return enabled ? enabledComponent : disabledComponent;
};

const useAsyncFlag = (flag: string, evaluate: AsyncFlagEvaluator, skip: boolean): FlagState => {
  const [state, setState] = useState<FlagState>({
    enabled: false,
    loading: !skip,
  });

  useEffect(() => {
    if (skip) {
      setState({ enabled: false, loading: false });
      return;
    }

    let stale = false;
    setState({ enabled: false, loading: true });

    void (async () => {
      try {
        const enabled = await evaluate(flag);
        if (stale) return;
        setState({ enabled, loading: false });
      } catch (error) {
        if (stale) return;
        setState({ enabled: false, loading: false });
        console.error('AsyncFeatureFlagGate: evaluator threw for flag', flag, error);
      }
    })();

    return () => {
      stale = true;
    };
  }, [evaluate, flag, skip]);

  return state;
};

export const AsyncFeatureFlagGate: React.FC<AsyncFeatureFlagGateProps> = ({
  flag,
  evaluate,
  enabled: enabledComponent,
  disabled: disabledComponent,
  loading: loadingComponent,
  forceDisabled = false,
}) => {
  const { enabled, loading } = useAsyncFlag(flag, evaluate, forceDisabled);

  if (forceDisabled) {
    return disabledComponent;
  }

  if (loading) {
    return loadingComponent ?? disabledComponent;
  }

  return enabled ? enabledComponent : disabledComponent;
};
